<template>
  <div>
    <h1>Lista de Concedentes</h1>

    <b-card>
      <b-alert :show="isError" variant="danger">
        {{ errorMessage }}
        <b-button size="sm" @click="loadData">Tentar novamente</b-button>
      </b-alert>
      <div class="d-flex mb-2">
        <div class="w-75 mr-auto">
          <b-input-group size="sm">
            <b-form-input
              v-model="termo_pesquisa"
              v-on:keyup.enter="searchTerm"
              placeholder="Pesquisar"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="searchTerm" variant="exitos">Buscar</b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div>
          <b-button
            variant="exitos"
            :to="{ name: 'concedentes_create' }"
            v-if="$can('concedente:create')"
          >
            <font-awesome-icon icon="plus" />
            Novo Concedente
          </b-button>
        </div>
      </div>

      <b-table
        hover
        :items="concedentes"
        :fields="fields"
        :busy="isBusy"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        no-local-sorting
        @sort-changed="sortingChanged"
        show-empty
        sort-icon-left
      >
        <template #cell(sigla)="{ item }">
          <CellAvatar :url="item?.logo" :label="item?.iniciais" />
        </template>
        <template #cell(nome)="{ item }">
          <CellName :concedente="item" />
        </template>
        <template #cell(endereco)="{ item }">
          <CellEndereco
            v-if="item.endereco"
            :endereco="item.endereco"
            :sem_endereco="item.sem_endereco"
          />
        </template>
        <template #cell(contatos)="{ item }">
          <CellContatos v-if="item.contatos" :contatos="item.contatos" />
        </template>
        <template #cell(actions)="row">
          <Actions
            :item="row.item"
            :view_show="false"
            :view_edit="$can('concedente:edit')"
            :view_delete="$can('concedente:delete')"
            edit_to="concedentes_edit"
            @delete="deleteItem"
            :delete_infors="oportunidadesInfors(row.item.oportunidades_count)"
          />
        </template>

        <template #empty="{}">
          <h5 class="text-center">Nenhum item encontado</h5>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </b-card>

    <div>
      <pagination-nav :pagination="pagination" @input="syncRoute" />
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, ref, watch } from "vue";
import { useNamespacedActions } from "vuex-composition-helpers";
import { useRoute, useRouter } from "vue-router/dist/composables.js";
import { useQuery } from "@/composables/useQuery";
import { useNotify } from "@/composables/useNotify";
import services from "@/http";
import Actions from "@/components/Table/Actions.vue";
import CellAvatar from "@/modules/concedentes/Components/CellAvatar.vue";
import CellName from "@/modules/concedentes/Components/CellName.vue";
import CellEndereco from "@/components/Table/CellEndereco.vue";
import CellContatos from "@/components/Table/CellContatos.vue";
import { extractErrorMessage } from "@/helpers/functions/errorHelpers";
import humanize_datetime from "@/helpers/filters/humanize_datetime";

export default defineComponent({
  name: "ConcedentesList",
  components: { Actions, CellAvatar, CellName, CellEndereco, CellContatos },
  setup() {
    const notifications = useNotify();
    const route = useRoute();
    const router = useRouter();
    const { ActionDelete } = useNamespacedActions("concedentes", [
      "ActionDelete",
    ]);

    const termo_pesquisa = ref(route.query.q || "");
    const search = ref(termo_pesquisa.value);
    const sortBy = ref("oportunidades_publicadas");
    const sortDesc = ref(true);
    const pagination = ref({
      current_page: Math.max(1, Number(route.query.page) || 1),
      per_page: 15,
      last_page: 1,
      total: 0,
      from: 0,
      to: 0,
    });

    const fields = [
      {
        key: "sigla",
        label: "",
        class: "col-auto text-center align-middle pr-0",
      },
      { key: "nome", label: "Nome", class: "align-middle", sortable: true },
      {
        key: "oportunidades_publicadas",
        label: "Oportunidades publicadas",
        class: "col-auto text-center align-middle",
        sortable: true,
      },
      {
        key: "contatos",
        label: "Formas de contato",
        class: "col-1 align-middle",
      },
      {
        key: "ultima_atualizacao",
        label: "Última atualização",
        class: "col-auto text-capitalize-first align-middle",
        sortable: true,
        formatter: (value) => humanize_datetime(value),
      },
      {
        key: "concedente_contatos_count",
        label: "Número de contatos",
        class: "col-1 align-middle",
      },
      { key: "actions", label: "", class: "cell-actions" },
    ];

    const {
      data,
      isFetching: isBusy,
      isError,
      error,
      refetch: loadData,
    } = useQuery({
      key: computed(() => [
        "concedentes",
        "lista",
        pagination.value.current_page,
        pagination.value.per_page,
        search.value,
        sortBy.value,
        sortDesc.value ? "desc" : "asc",
      ]),
      queryFn: () =>
        services.concedentes.get_all({
          page: pagination.value.current_page,
          per_page: pagination.value.per_page,
          orderBy: sortBy.value,
          sortBy: sortDesc.value ? "desc" : "asc",
          q: search.value,
          incluir_sem_oportunidade: true,
        }),
      select: (response) => response.data,
    });

    const concedentes = computed(() => data.value?.data || []);
    const errorMessage = computed(() =>
      extractErrorMessage(error.value || {}, "Erro ao carregar a listagem.")
    );

    function syncRoute() {
      const page = pagination.value.current_page;
      if (
        (Number(route.query.page) || 1) !== page ||
        (route.query.q || "") !== search.value
      ) {
        router.push({ query: { ...route.query, page, q: search.value } });
      }
    }

    function searchTerm() {
      const unchanged =
        search.value === termo_pesquisa.value &&
        pagination.value.current_page === 1;

      search.value = termo_pesquisa.value;
      pagination.value.current_page = 1;
      syncRoute();
      if (unchanged) {
        loadData();
      }
    }

    function sortingChanged(value) {
      sortBy.value = value.sortBy || "oportunidades_publicadas";
      sortDesc.value = value.sortDesc;
      pagination.value.current_page = 1;
      syncRoute();
    }

    async function deleteItem(item) {
      try {
        await ActionDelete(item.id);
        notifications.addNotification({
          message: "Registro deletado com sucesso",
          type: "success",
        });

        await loadData();
      } catch (error) {
        notifications.addNotification({
          message: extractErrorMessage(error, "Erro ao excluir registro"),
          type: "danger",
        });
      }
    }

    function oportunidadesInfors(count) {
      if (count) {
        return `Esse concedente possui ${count} oportunidade(s) vinculada(s). Tem certeza de que deseja excluir?`;
      }

      return "";
    }

    watch(data, (result) => {
      if (!result?.meta) return;

      for (const key of ["last_page", "total", "from", "to"]) {
        pagination.value[key] = result.meta[key] ?? 0;
      }
    });

    watch(
      () => [route.query.page, route.query.q],
      () => {
        pagination.value.current_page = Math.max(
          1,
          Number(route.query.page) || 1
        );

        search.value = route.query.q || "";
        termo_pesquisa.value = search.value;
      }
    );

    return {
      concedentes,
      pagination,
      termo_pesquisa,
      sortBy,
      sortDesc,
      isBusy,
      isError,
      errorMessage,
      syncRoute,
      fields,
      loadData,
      searchTerm,
      sortingChanged,
      deleteItem,
      oportunidadesInfors,
    };
  },
});
</script>
